import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../../../components/SEO';
import RelatedBlogPosts from '../../../components/feature/RelatedBlogPosts';

export default function OakvilleServiceAreaPage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const services = [
    {
      icon: 'ri-shield-check-line',
      title: 'Alarm Systems Oakville',
      description: 'Professional alarm system installation and monitoring for Oakville homes and businesses. 24/7 protection with instant mobile alerts.',
      link: '/alarm-packages'
    },
    {
      icon: 'ri-camera-line',
      title: 'Security Cameras Oakville',
      description: 'HD and 4K security camera systems with night vision, motion detection, and cloud storage for Oakville properties.',
      link: '/camera-packages'
    },
    {
      icon: 'ri-key-2-line',
      title: 'Access Control Oakville',
      description: 'Commercial card access and keyless entry systems for Oakville businesses. Secure entry management with detailed logs.',
      link: '/access-control'
    },
    {
      icon: 'ri-live-line',
      title: 'Live Monitoring Oakville',
      description: 'Real-time video surveillance monitoring for Oakville properties. Watch your property from anywhere, anytime.',
      link: '/live-monitoring'
    }
  ];

  const neighborhoods = [
    'Downtown Oakville', 'Bronte', 'Glen Abbey', 'River Oaks', 'Clearview',
    'Eastlake', 'Palermo', 'Joshua Creek', 'Iroquois Ridge', 'College Park',
    'Uptown Core', 'Old Oakville', 'West Oak Trails', 'Falgarwood', 'Sixteen Mile'
  ];

  const stats = [
    { number: '2,400+', label: 'Oakville Installations' },
    { number: '25+', label: 'Years Serving Oakville' },
    { number: '4.9', label: 'Google Rating' },
    { number: '< 1hr', label: 'Local Response Time' }
  ];

  const localInsights = [
    {
      icon: 'ri-home-heart-line',
      stat: '$1.2M',
      label: 'Average Oakville Home Value',
      description: 'Oakville\'s luxury real estate market demands premium security solutions. High-value homes in Glen Abbey, River Oaks, and Old Oakville require advanced camera systems, smart integration, and comprehensive protection.'
    },
    {
      icon: 'ri-ship-line',
      stat: '78%',
      label: 'Lakefront Properties Need Enhanced Security',
      description: 'Oakville\'s prestigious Lake Ontario waterfront homes face unique security challenges with multiple access points, large properties, and seasonal occupancy requiring sophisticated monitoring and access control systems.'
    },
    {
      icon: 'ri-shield-star-line',
      stat: '15-20%',
      label: 'Insurance Discount with Professional Security',
      description: 'Oakville homeowners with professionally monitored security systems qualify for significant insurance premium reductions, often saving $300-$800 annually while protecting valuable assets.'
    }
  ];

  const faqs = [
    {
      question: 'Do you serve all Oakville neighborhoods including Glen Abbey and Bronte?',
      answer: 'Yes! We serve all Oakville neighborhoods including Downtown Oakville, Bronte, Glen Abbey, River Oaks, Clearview, Eastlake, Palermo, Joshua Creek, Iroquois Ridge, College Park, Uptown Core, Old Oakville, West Oak Trails, Falgarwood, and Sixteen Mile. We specialize in luxury home security and understand Oakville\'s unique property requirements.'
    },
    {
      question: 'What security systems do you recommend for luxury Oakville homes?',
      answer: 'For Oakville\'s luxury homes, we recommend comprehensive systems including: 4K security cameras with advanced analytics, smart home integration (Control4, Crestron compatible), perimeter protection with glass break sensors, smart locks and access control, environmental monitoring (water, temperature), and 24/7 professional monitoring. We design custom solutions that blend seamlessly with high-end home aesthetics.'
    },
    {
      question: 'Can you integrate security with my Oakville smart home system?',
      answer: 'Absolutely! We specialize in smart home security integration for Oakville properties. Our systems work seamlessly with popular platforms like Control4, Crestron, Savant, Google Home, Amazon Alexa, and Apple HomeKit. We can integrate alarms, cameras, door locks, lighting, and climate control into one unified system controlled from your smartphone or tablet.'
    }
  ];

  const structuredData = [
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://alliancesecuritysystems.com/' },
        { '@type': 'ListItem', position: 2, name: 'Service Areas', item: 'https://alliancesecuritysystems.com/service-areas' },
        { '@type': 'ListItem', position: 3, name: 'Oakville', item: 'https://alliancesecuritysystems.com/service-areas/oakville' }
      ]
    },
    {
      '@context': 'https://schema.org',
      '@type': 'LocalBusiness',
      '@id': 'https://alliancesecuritysystems.com/service-areas/oakville#business',
      name: 'Alliance Security Systems - Oakville',
      image: 'https://public.readdy.ai/ai/img_res/018a47667d423e2338cd39050015277b.jpg',
      telephone: '+1-888-458-9181',
      address: {
        '@type': 'PostalAddress',
        streetAddress: '2355 Derry Road East, Unit 1',
        addressLocality: 'Mississauga',
        addressRegion: 'ON',
        postalCode: 'L5S 1V6',
        addressCountry: 'CA'
      },
      geo: { '@type': 'GeoCoordinates', latitude: '43.4675', longitude: '-79.6877' },
      url: 'https://alliancesecuritysystems.com/service-areas/oakville',
      priceRange: '$$$',
      areaServed: {
        '@type': 'City',
        name: 'Oakville',
        containedInPlace: { '@type': 'AdministrativeArea', name: 'Ontario' }
      },
      hasOfferCatalog: {
        '@type': 'OfferCatalog',
        name: 'Oakville Security Services',
        itemListElement: [
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Alarm Systems Oakville', description: 'Professional alarm system installation and 24/7 monitoring for Oakville luxury homes and businesses.' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Security Cameras Oakville', description: '4K security camera installation with advanced analytics for Oakville properties.' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Access Control Oakville', description: 'Premium access control and smart lock systems for Oakville homes and businesses.' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Smart Home Security Oakville', description: 'Integrated smart home security solutions for luxury Oakville properties.' } }
        ]
      },
      aggregateRating: { '@type': 'AggregateRating', ratingValue: '4.9', reviewCount: '320', bestRating: '5' }
    }
  ];

  return (
    <>
      <SEO
        title="Security Systems Oakville | Luxury Home Security | Smart Integration Experts"
        description="Oakville's trusted luxury home security company since 1999. Professional alarm systems, 4K cameras, and smart home integration. 2,400+ Oakville installations. Free consultation."
        keywords="security systems Oakville, alarm systems Oakville, security cameras Oakville, luxury home security Oakville, smart home security Oakville, Glen Abbey security, Bronte security"
        canonical="/service-areas/oakville"
        structuredData={structuredData}
        faqItems={faqs}
      />

      <div className="min-h-screen bg-white">
        {/* Navigation */}
        <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-md' : 'bg-transparent'}`}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              <div className="flex flex-col leading-none">
                <span className="text-2xl sm:text-3xl font-extrabold tracking-wider text-[#D4A853]" style={{ fontFamily: "'Inter', sans-serif" }}>ALLIANCE</span>
                <span className="text-[10px] sm:text-xs font-semibold tracking-[0.25em] text-[#D4A853]">SECURITY SYSTEMS</span>
              </div>
            </Link>
            <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="md:hidden w-10 h-10 flex items-center justify-center cursor-pointer">
              <i className={`${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl ${isScrolled ? 'text-gray-700' : 'text-white'}`}></i>
            </button>
            <div className="hidden md:flex items-center gap-6">
              <Link to="/" className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${isScrolled ? 'text-gray-700 hover:text-[#D4A853]' : 'text-white/90 hover:text-white'}`}>
                Home
              </Link>
              <Link to="/service-areas" className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${isScrolled ? 'text-[#D4A853]' : 'text-white'}`}>
                Service Areas
              </Link>
              <a href="tel:18884589181" className="bg-[#D4A853] text-white px-6 py-2 rounded-full text-sm font-semibold hover:bg-[#B8923D] transition-all whitespace-nowrap cursor-pointer">
                1-888-458-9181
              </a>
            </div>
          </div>
          {mobileMenuOpen && (
            <div className={`md:hidden ${isScrolled ? 'bg-white' : 'bg-[#0A1628]/95 backdrop-blur-sm'} border-t ${isScrolled ? 'border-gray-100' : 'border-white/10'} px-4 pb-4 space-y-2`}>
              <Link to="/" className={`block py-3 text-sm font-medium border-b ${isScrolled ? 'text-gray-700 border-gray-50' : 'text-white/90 border-white/10'}`}>Home</Link>
              <Link to="/service-areas" className={`block py-3 text-sm font-medium border-b ${isScrolled ? 'text-[#D4A853] border-gray-50' : 'text-[#D4A853] border-white/10'}`}>Service Areas</Link>
              <a href="tel:18884589181" className="block w-full text-center bg-[#D4A853] text-white px-6 py-3 rounded-full text-sm font-semibold hover:bg-[#B8923D] transition-all cursor-pointer whitespace-nowrap mt-2">1-888-458-9181</a>
            </div>
          )}
        </nav>

        {/* Hero Section */}
        <section className="relative min-h-[50vh] sm:min-h-[70vh] flex items-center pt-16 sm:pt-20">
          <div className="absolute inset-0">
            <img 
              src="https://readdy.ai/api/search-image?query=Oakville%20Ontario%20lakefront%20harbor%20with%20luxury%20homes%20and%20boats%20beautiful%20waterfront%20community%20professional%20photography%20warm%20golden%20hour%20lighting&width=1920&height=1080&seq=oak1&orientation=landscape" 
              alt="Oakville Waterfront"
              className="w-full h-full object-cover object-center"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-black/40"></div>
          </div>
          
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 w-full py-12 sm:py-20">
            <div className="max-w-3xl">
              <div className="flex items-center gap-2 mb-3 sm:mb-4">
                <i className="ri-map-pin-2-fill text-[#D4A853] text-lg sm:text-xl"></i>
                <span className="text-[#D4A853] font-semibold text-sm sm:text-base">Oakville, Ontario</span>
              </div>
              <h1 className="text-3xl sm:text-5xl md:text-6xl font-extrabold text-white mb-4 sm:mb-6 leading-tight">
                <strong>Security Systems Oakville</strong> - Premium Protection for Luxury Homes
              </h1>
              <p className="text-base sm:text-xl text-white/90 mb-6 sm:mb-8">
                Oakville's affluent lakefront community demands the highest level of security sophistication. With over 2,400 installations across Glen Abbey, Bronte, River Oaks, and Old Oakville, we specialize in luxury home security with smart integration, 4K cameras, and discreet protection that complements your property's elegance. Our expertise in high-value properties ensures your family and assets receive premium protection.
              </p>
              <div className="flex flex-col sm:flex-row flex-wrap items-start sm:items-center gap-3 sm:gap-4">
                <Link 
                  to="/#contact"
                  className="w-full sm:w-auto bg-[#D4A853] text-white px-6 sm:px-8 py-3.5 sm:py-4 rounded-full text-sm sm:text-base font-semibold hover:bg-[#B8923D] transition-all flex items-center justify-center gap-3 whitespace-nowrap cursor-pointer shadow-lg"
                >
                  Get Free Quote in Oakville
                  <span className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                    <i className="ri-arrow-right-line text-lg"></i>
                  </span>
                </Link>
                <a 
                  href="tel:18884589181"
                  className="w-full sm:w-auto border-2 border-white text-white px-6 sm:px-8 py-3.5 sm:py-4 rounded-full text-sm sm:text-base font-semibold hover:bg-white hover:text-gray-900 transition-all whitespace-nowrap cursor-pointer text-center"
                >
                  <i className="ri-phone-line mr-2"></i>
                  Call Now
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Breadcrumb */}
        <nav className="bg-white border-b border-gray-100 py-3" aria-label="Breadcrumb">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <ol className="flex items-center gap-2 text-xs text-gray-500" itemScope itemType="https://schema.org/BreadcrumbList">
              <li itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
                <Link to="/" className="hover:text-[#D4A853] transition-colors cursor-pointer" itemProp="item">
                  <span itemProp="name">Home</span>
                </Link>
                <meta itemProp="position" content="1" />
              </li>
              <li className="flex items-center"><i className="ri-arrow-right-s-line text-gray-400"></i></li>
              <li itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
                <Link to="/service-areas" className="hover:text-[#D4A853] transition-colors cursor-pointer" itemProp="item">
                  <span itemProp="name">Service Areas</span>
                </Link>
                <meta itemProp="position" content="2" />
              </li>
              <li className="flex items-center"><i className="ri-arrow-right-s-line text-gray-400"></i></li>
              <li itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
                <span className="text-[#D4A853] font-medium" itemProp="name">Oakville</span>
                <meta itemProp="position" content="3" />
              </li>
            </ol>
          </div>
        </nav>

        {/* Stats Section */}
        <section className="py-8 sm:py-12 bg-[#0A1628]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-8">
              {stats.map((stat, index) => (
                <div key={index} className="text-center">
                  <p className="text-2xl sm:text-4xl font-bold text-[#D4A853] mb-1 sm:mb-2">{stat.number}</p>
                  <p className="text-white/70 text-xs sm:text-sm">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Local Security Insights */}
        <section className="py-12 sm:py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-10 sm:mb-16">
              <span className="inline-block bg-[#D4A853]/10 text-[#D4A853] px-4 sm:px-6 py-2 rounded-full text-xs sm:text-sm font-semibold mb-3 sm:mb-4 whitespace-nowrap">
                OAKVILLE SECURITY INSIGHTS
              </span>
              <h2 className="text-2xl sm:text-4xl font-bold text-[#0A1628] mb-3 sm:mb-4">
                Why Oakville Properties Need Professional Security
              </h2>
              <p className="text-sm sm:text-lg text-gray-600 max-w-2xl mx-auto">
                Oakville-specific security challenges and solutions
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8">
              {localInsights.map((insight, index) => (
                <div key={index} className="bg-white rounded-xl sm:rounded-2xl p-6 sm:p-8 border border-gray-100 hover:shadow-xl transition-all">
                  <div className="w-14 h-14 sm:w-16 sm:h-16 bg-[#D4A853]/10 rounded-full flex items-center justify-center mb-4 sm:mb-6">
                    <i className={`${insight.icon} text-2xl sm:text-3xl text-[#D4A853]`}></i>
                  </div>
                  <p className="text-3xl sm:text-4xl font-bold text-[#D4A853] mb-2">{insight.stat}</p>
                  <h3 className="text-lg sm:text-xl font-bold text-[#0A1628] mb-3">{insight.label}</h3>
                  <p className="text-gray-600 text-sm sm:text-base leading-relaxed">{insight.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Services Section */}
        <section className="py-12 sm:py-24 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-10 sm:mb-16">
              <span className="inline-block bg-[#D4A853]/10 text-[#D4A853] px-4 sm:px-6 py-2 rounded-full text-xs sm:text-sm font-semibold mb-3 sm:mb-4 whitespace-nowrap">
                OAKVILLE SECURITY SERVICES
              </span>
              <h2 className="text-2xl sm:text-4xl font-bold text-[#0A1628] mb-3 sm:mb-4">
                Professional Security Solutions in Oakville
              </h2>
              <p className="text-sm sm:text-lg text-gray-600 max-w-2xl mx-auto">
                Comprehensive security services tailored for Oakville homes and businesses
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 sm:gap-8">
              {services.map((service, index) => (
                <div 
                  key={index}
                  className="bg-gray-50 rounded-xl sm:rounded-2xl p-5 sm:p-8 hover:shadow-xl transition-all duration-300 border border-gray-100"
                >
                  <div className="w-12 h-12 sm:w-16 sm:h-16 bg-[#D4A853] rounded-full flex items-center justify-center mb-4 sm:mb-6">
                    <i className={`${service.icon} text-2xl sm:text-3xl text-white`}></i>
                  </div>
                  <h3 className="text-xl sm:text-2xl font-bold text-[#0A1628] mb-3 sm:mb-4">{service.title}</h3>
                  <p className="text-gray-600 mb-4 sm:mb-6 leading-relaxed text-sm sm:text-base">{service.description}</p>
                  <Link to={service.link} className="text-[#D4A853] font-semibold flex items-center gap-2 hover:gap-3 transition-all cursor-pointer whitespace-nowrap text-sm sm:text-base">
                    Learn More <i className="ri-arrow-right-line"></i>
                  </Link>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-12 sm:py-20 bg-gray-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-10 sm:mb-12">
              <span className="inline-block bg-[#D4A853]/10 text-[#D4A853] px-4 sm:px-6 py-2 rounded-full text-xs sm:text-sm font-semibold mb-3 sm:mb-4 whitespace-nowrap">
                OAKVILLE FAQ
              </span>
              <h2 className="text-2xl sm:text-4xl font-bold text-[#0A1628] mb-3 sm:mb-4">
                Frequently Asked Questions About Oakville Security
              </h2>
            </div>

            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <div key={index} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                  <button
                    onClick={() => setOpenFaq(openFaq === index ? null : index)}
                    className="w-full px-6 py-5 flex items-center justify-between text-left hover:bg-gray-50 transition-colors cursor-pointer"
                  >
                    <span className="text-base sm:text-lg font-semibold text-[#0A1628] pr-4">{faq.question}</span>
                    <i className={`ri-arrow-${openFaq === index ? 'up' : 'down'}-s-line text-xl text-[#D4A853] flex-shrink-0`}></i>
                  </button>
                  {openFaq === index && (
                    <div className="px-6 pb-5 text-gray-600 text-sm sm:text-base leading-relaxed border-t border-gray-100 pt-4">
                      {faq.answer}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Related Blog Posts */}
        <RelatedBlogPosts city="oakville" />

        {/* Oakville Security Resource Centre */}
        <section className="py-16 sm:py-24 bg-[#0A1628]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-12">
              <div className="inline-flex items-center justify-center px-4 py-2 bg-[#D4A853]/20 rounded-full mb-4">
                <i className="ri-book-open-line text-[#D4A853] mr-2"></i>
                <span className="text-sm font-semibold text-[#D4A853] uppercase tracking-wide">Oakville Security Resource Centre</span>
              </div>
              <h2 className="text-2xl sm:text-4xl font-bold text-white mb-4">
                The Complete Oakville Security Library
              </h2>
              <p className="text-white/70 text-base sm:text-lg max-w-2xl mx-auto">
                Independent, research-based guides covering every aspect of residential and commercial security in Oakville and Halton Region
              </p>
            </div>

            {/* Two Residential Guide Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              {[
                {
                  badge: 'Home Security Guide',
                  badgeIcon: 'ri-home-heart-line',
                  title: 'Best Home Security Company in Oakville: The Definitive 2026 Ranking & Buyers Guide',
                  description: 'Covers luxury estate security in Old Oakville and Morrison, organized targeted break-ins, Halton Regional Police response protocols, vehicle theft in the QEW corridor, and the exact evaluation criteria that separate the best Oakville home security companies from GTA generalists.',
                  tags: ['Luxury Estate Security', 'HRP Protocols', 'Vehicle Theft', 'ULC Monitoring'],
                  slug: 'best-home-security-company-oakville',
                  image: 'https://readdy.ai/api/search-image?query=upscale%20Oakville%20Ontario%20lakefront%20neighbourhood%20at%20golden%20hour%20luxury%20detached%20stone%20home%20with%20professional%20security%20camera%20mounted%20above%20grand%20entrance%20warm%20porch%20lighting%20manicured%20hedges%20mature%20oak%20trees%20quiet%20affluent%20residential%20street%20near%20Lake%20Ontario%20Halton%20Region%20Canada%20clear%20evening%20sky&width=600&height=380&seq=blog27&orientation=landscape',
                },
                {
                  badge: 'Alarm System Guide',
                  badgeIcon: 'ri-alarm-warning-line',
                  title: 'Best Alarm System Company in Oakville: The Complete 2025 Guide',
                  description: 'Covers ULC-certified monitoring standards, Halton Regional Police false alarm fee schedules, neighbourhood risk profiles from Glen Abbey to Morrison and the Preserve, contract evaluation criteria, and why equipment ownership is non-negotiable in Oakville\'s premium alarm market.',
                  tags: ['ULC Certification', 'False Alarm Management', 'Neighbourhood Risk', 'Contract Terms'],
                  slug: 'best-alarm-system-company-oakville',
                  image: 'https://readdy.ai/api/search-image?query=upscale%20Oakville%20Ontario%20lakeside%20neighbourhood%20at%20golden%20hour%20large%20detached%20home%20with%20professional%20security%20system%20panel%20glowing%20near%20front%20entrance%20manicured%20lawn%20mature%20trees%20quiet%20residential%20street%20warm%20amber%20light%20affluent%20community%20near%20Lake%20Ontario&width=600&height=380&seq=blog21&orientation=landscape',
                },
              ].map((guide, i) => (
                <a key={i} href={`/blog/${guide.slug}`} className="group bg-white/5 border border-white/10 rounded-xl overflow-hidden hover:bg-white/10 transition-all duration-300">
                  <div className="relative h-52 overflow-hidden">
                    <img src={guide.image} alt={guide.title} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                    <div className="absolute top-4 left-4 flex items-center gap-2 bg-[#D4A853] text-white text-xs font-semibold px-3 py-1.5 rounded-full">
                      <i className={`${guide.badgeIcon}`}></i>
                      <span>{guide.badge}</span>
                    </div>
                  </div>
                  <div className="p-6">
                    <h3 className="text-white font-bold text-base sm:text-lg mb-2 group-hover:text-[#D4A853] transition-colors line-clamp-2">{guide.title}</h3>
                    <p className="text-white/60 text-sm mb-4 line-clamp-3">{guide.description}</p>
                    <div className="flex flex-wrap gap-2 mb-4">
                      {guide.tags.map((tag, t) => (
                        <span key={t} className="text-xs bg-white/10 text-white/70 px-2 py-1 rounded-full">{tag}</span>
                      ))}
                    </div>
                    <div className="flex items-center gap-2 text-[#D4A853] text-sm font-semibold group-hover:gap-3 transition-all">
                      <span>Read Full Guide</span>
                      <i className="ri-arrow-right-line"></i>
                    </div>
                  </div>
                </a>
              ))}
            </div>

            {/* Commercial Guide — Full Width Landscape Card */}
            <a href="/blog/commercial-security-company-oakville" className="group block bg-white/5 border border-white/10 rounded-xl overflow-hidden hover:bg-white/10 transition-all duration-300 mb-8">
              <div className="flex flex-col md:flex-row">
                <div className="relative md:w-80 lg:w-96 h-56 md:h-auto overflow-hidden flex-shrink-0">
                  <img
                    src="https://readdy.ai/api/search-image?query=Oakville%20Ontario%20Trafalgar%20Road%20commercial%20and%20industrial%20district%20at%20dusk%20modern%20business%20park%20buildings%20professional%20security%20cameras%20mounted%20on%20exterior%20walls%20QEW%20highway%20visible%20in%20background%20clean%20corporate%20architecture%20Halton%20Region%20Canada%20clear%20evening%20sky&width=600&height=400&seq=blog30&orientation=landscape"
                    alt="Best Commercial Security Company Oakville"
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent to-black/40"></div>
                  <div className="absolute top-4 left-4 flex items-center gap-2 bg-amber-500 text-white text-xs font-semibold px-3 py-1.5 rounded-full">
                    <i className="ri-building-2-line"></i>
                    <span>Commercial Security Guide</span>
                  </div>
                </div>
                <div className="p-6 sm:p-8 flex flex-col justify-center flex-1">
                  <h3 className="text-white font-bold text-lg sm:text-xl mb-3 group-hover:text-[#D4A853] transition-colors">
                    Best Commercial Security Company in Oakville: The Definitive 2026 Guide for Business &amp; Industrial Properties
                  </h3>
                  <p className="text-white/60 text-sm mb-4 line-clamp-3">
                    Covers the Trafalgar industrial corridor and auto-sector corporate campus security, QEW commercial zone vehicle theft and LPR deployment, organized retail crime countermeasures for the Lakeshore luxury retail corridor, and Halton Regional Police commercial alarm protocols.
                  </p>
                  <div className="flex flex-wrap gap-2 mb-5">
                    {['Trafalgar Corridor', 'ORC Countermeasures', 'LPR Cameras', 'Corporate Access Control', 'HRP Protocols'].map((tag, t) => (
                      <span key={t} className="text-xs bg-white/10 text-white/70 px-2 py-1 rounded-full">{tag}</span>
                    ))}
                  </div>
                  <div className="flex items-center gap-2 text-[#D4A853] text-sm font-semibold group-hover:gap-3 transition-all">
                    <span>Read Full Guide</span>
                    <i className="ri-arrow-right-line"></i>
                  </div>
                </div>
              </div>
            </a>

            {/* Trust Strip */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {[
                { icon: 'ri-search-eye-line', label: 'Independent Research', desc: 'Not sponsored or affiliate-driven — written by certified Halton Region security consultants' },
                { icon: 'ri-map-pin-2-line', label: 'Oakville-Specific', desc: 'Every guide is designed specifically for Oakville\'s crime profile, neighbourhoods, and HRP protocols' },
                { icon: 'ri-refresh-line', label: 'Updated 2026', desc: 'Reflects current HRP statistics, commercial crime data, and Ontario insurance discount schedules' },
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-3 bg-white/5 rounded-lg p-4">
                  <div className="w-9 h-9 bg-[#D4A853]/20 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className={`${item.icon} text-[#D4A853] text-lg`}></i>
                  </div>
                  <div>
                    <p className="text-white font-semibold text-sm">{item.label}</p>
                    <p className="text-white/55 text-xs mt-0.5">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Why a Local Expert Matters */}
        <section className="py-12 sm:py-24 bg-[#0A1628]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-10 sm:mb-16">
              <span className="inline-block bg-[#D4A853]/20 text-[#D4A853] px-4 sm:px-6 py-2 rounded-full text-xs sm:text-sm font-semibold mb-3 sm:mb-4 whitespace-nowrap">LOCAL ADVANTAGE</span>
              <h2 className="text-2xl sm:text-4xl font-bold text-white mb-3 sm:mb-4">Why a Local Security Expert Matters in Oakville</h2>
              <p className="text-sm sm:text-lg text-white/70 max-w-2xl mx-auto">National chains can't match the speed, knowledge, and accountability of a true local provider</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 sm:gap-6">
              {[
                {
                  icon: 'ri-timer-flash-line',
                  title: 'Under 1-Hour Response',
                  description: 'Our Mississauga office is minutes from Oakville — not a distant call centre. Oakville emergencies get fast, local responses across all neighbourhoods including Glen Abbey and Bronte.',
                },
                {
                  icon: 'ri-government-line',
                  title: 'Oakville Bylaw Knowledge',
                  description: 'We know Halton Region\'s alarm permit requirements, false alarm bylaws, and Town of Oakville regulations — so your luxury home system stays fully compliant.',
                },
                {
                  icon: 'ri-user-star-line',
                  title: 'Local References',
                  description: '"Alliance installed a discreet 4K camera system in our Old Oakville home — they matched the heritage aesthetic perfectly and the smart integration is flawless." — Oakville client',
                },
                {
                  icon: 'ri-percent-line',
                  title: 'Ontario Insurance Discounts',
                  description: 'Monitored security systems qualify for up to 20% home insurance discounts in Ontario. For Oakville\'s high-value homes, that can mean $500–$1,200 in annual savings.',
                },
              ].map((point, i) => (
                <div key={i} className="bg-white/5 border border-white/10 rounded-xl p-6 sm:p-8 hover:bg-white/10 transition-all">
                  <div className="w-12 h-12 bg-[#D4A853]/20 rounded-full flex items-center justify-center mb-4">
                    <i className={`${point.icon} text-2xl text-[#D4A853]`}></i>
                  </div>
                  <h3 className="text-base sm:text-lg font-bold text-white mb-2 sm:mb-3">{point.title}</h3>
                  <p className="text-white/65 text-xs sm:text-sm leading-relaxed">{point.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Why Choose Us Section */}
        <section className="py-12 sm:py-24 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
              <div>
                <span className="inline-block bg-[#D4A853]/10 text-[#D4A853] px-4 sm:px-6 py-2 rounded-full text-xs sm:text-sm font-semibold mb-3 sm:mb-4 whitespace-nowrap">
                  WHY OAKVILLE TRUSTS US
                </span>
                <h2 className="text-2xl sm:text-4xl font-bold text-[#0A1628] mb-4 sm:mb-6">
                  Oakville's Premier Luxury Home Security Company
                </h2>
                <p className="text-gray-600 mb-6 sm:mb-8 leading-relaxed text-sm sm:text-base">
                  For over 25 years, we've been protecting Oakville's most prestigious properties with discreet, sophisticated security solutions.
                </p>
                
                <div className="space-y-3 sm:space-y-4">
                  {[
                    'Luxury home security specialists',
                    'Smart home integration experts (Control4, Crestron)',
                    'Discreet installations that complement home aesthetics',
                    'Licensed and insured Oakville security professionals',
                    '24/7 premium monitoring and concierge support',
                    'Free on-site security assessments'
                  ].map((item, index) => (
                    <div key={index} className="flex items-center gap-3">
                      <div className="w-5 h-5 sm:w-6 sm:h-6 bg-[#D4A853] rounded-full flex items-center justify-center flex-shrink-0">
                        <i className="ri-check-line text-white text-xs sm:text-sm"></i>
                      </div>
                      <span className="text-gray-700 text-sm sm:text-base">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="relative w-full h-[300px] sm:h-[500px] rounded-2xl overflow-hidden">
                <img 
                  src="https://readdy.ai/api/search-image?query=professional%20security%20technician%20installing%20discreet%20camera%20system%20in%20elegant%20luxury%20Oakville%20home%20interior%20sophisticated%20modern%20design%20high-end%20finishes&width=800&height=600&seq=oakville2&orientation=landscape" 
                  alt="Security Installation Oakville"
                  className="w-full h-full object-cover object-center"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Neighborhoods Section */}
        <section className="py-12 sm:py-24 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-10 sm:mb-16">
              <span className="inline-block bg-[#D4A853]/10 text-[#D4A853] px-4 sm:px-6 py-2 rounded-full text-xs sm:text-sm font-semibold mb-3 sm:mb-4 whitespace-nowrap">
                SERVICE COVERAGE
              </span>
              <h2 className="text-2xl sm:text-4xl font-bold text-[#0A1628] mb-3 sm:mb-4">
                Oakville Neighborhoods We Serve
              </h2>
              <p className="text-sm sm:text-lg text-gray-600 max-w-2xl mx-auto">
                Providing security services across all Oakville neighborhoods
              </p>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4">
              {neighborhoods.map((neighborhood, index) => (
                <div 
                  key={index}
                  className="bg-white rounded-lg sm:rounded-xl p-3 sm:p-4 text-center hover:bg-[#D4A853]/10 hover:border-[#D4A853]/30 border border-gray-100 transition-all duration-300"
                >
                  <span className="text-[#0A1628] font-medium text-xs sm:text-sm">{neighborhood}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Office Location & Map Section */}
        <section className="py-12 sm:py-24 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-10 sm:mb-12">
              <span className="inline-block bg-[#D4A853]/10 text-[#D4A853] px-4 sm:px-6 py-2 rounded-full text-xs sm:text-sm font-semibold mb-3 sm:mb-4 whitespace-nowrap">
                OUR LOCATION
              </span>
              <h2 className="text-2xl sm:text-4xl font-bold text-[#0A1628] mb-3 sm:mb-4">
                Serving Oakville from Our Mississauga Office
              </h2>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-start">
              <div className="bg-gray-50 rounded-2xl p-6 sm:p-8 border border-gray-100">
                <div className="flex items-start gap-4 mb-6">
                  <div className="w-12 h-12 bg-[#D4A853] rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-map-pin-2-fill text-white text-2xl"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-[#0A1628] mb-2">Office Address</h3>
                    <p className="text-gray-700 leading-relaxed">
                      2355 Derry Road East, Unit 1<br />
                      Mississauga, ON L5S 1V6
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4 mb-6">
                  <div className="w-12 h-12 bg-[#D4A853] rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-phone-fill text-white text-2xl"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-[#0A1628] mb-2">Phone</h3>
                    <a href="tel:18884589181" className="text-[#D4A853] font-semibold text-lg hover:underline cursor-pointer">
                      1-888-458-9181
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4 mb-8">
                  <div className="w-12 h-12 bg-[#D4A853] rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-time-fill text-white text-2xl"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-[#0A1628] mb-2">Business Hours</h3>
                    <p className="text-gray-700 leading-relaxed">
                      Monday - Friday: 8:00 AM - 6:00 PM<br />
                      Saturday: 9:00 AM - 5:00 PM (By Appointment)<br />
                      Sunday: Closed
                    </p>
                  </div>
                </div>

                <Link
                  to="/#contact"
                  className="w-full bg-[#D4A853] text-white px-8 py-4 rounded-full text-base font-semibold hover:bg-[#B8923D] transition-all cursor-pointer whitespace-nowrap text-center flex items-center justify-center gap-2"
                >
                  Schedule Consultation
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </div>

              <div className="w-full h-[400px] sm:h-[500px] rounded-2xl overflow-hidden shadow-xl">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d92166.89474469384!2d-79.76!3d43.467!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x882b469fe76b05b7%3A0x3146cbed75966db!2sOakville%2C%20ON!5e0!3m2!1sen!2sca!4v1234567890"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Oakville Service Area Map"
                ></iframe>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-12 sm:py-24 bg-[#0A1628]">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
            <h2 className="text-2xl sm:text-4xl font-bold text-white mb-4 sm:mb-6">
              Ready to Secure Your Oakville Property?
            </h2>
            <p className="text-base sm:text-xl text-white/80 mb-6 sm:mb-8">
              Get a free security assessment from Oakville's luxury home security experts
            </p>
            <div className="flex flex-col sm:flex-row flex-wrap items-center justify-center gap-3 sm:gap-4">
              <Link 
                to="/#contact"
                className="w-full sm:w-auto bg-[#D4A853] text-white px-8 sm:px-10 py-3.5 sm:py-4 rounded-full text-base sm:text-lg font-semibold hover:bg-[#B8923D] transition-all cursor-pointer whitespace-nowrap text-center"
              >
                Schedule Free Consultation
              </Link>
              <a 
                href="tel:18884589181"
                className="w-full sm:w-auto border-2 border-white text-white px-8 sm:px-10 py-3.5 sm:py-4 rounded-full text-base sm:text-lg font-semibold hover:bg-white hover:text-[#0A1628] transition-all cursor-pointer whitespace-nowrap text-center"
              >
                1-888-458-9181
              </a>
            </div>
          </div>
        </section>

        {/* Commercial Security Cross-Link Banner */}
        <section className="py-12 bg-gradient-to-r from-[#D4A853] to-[#B8923D]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="text-center md:text-left">
                <div className="flex items-center justify-center md:justify-start gap-2 mb-2">
                  <i className="ri-building-2-line text-white text-2xl"></i>
                  <h3 className="text-xl sm:text-2xl font-bold text-white">Looking for Commercial Security in Oakville?</h3>
                </div>
                <p className="text-white/90 text-sm sm:text-base">
                  Explore our dedicated commercial security solutions for Oakville businesses — cameras, alarms, access control & monitoring.
                </p>
              </div>
              <Link
                to="/commercial-security/oakville"
                className="bg-white text-[#D4A853] px-6 sm:px-8 py-3 sm:py-3.5 rounded-full text-sm sm:text-base font-semibold hover:bg-gray-100 transition-all cursor-pointer whitespace-nowrap shadow-lg flex items-center gap-2"
              >
                View Commercial Security Oakville <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        </section>

        {/* Other Service Areas */}
        <section className="py-10 sm:py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <h3 className="text-xl sm:text-2xl font-bold text-[#1E293B] mb-6 sm:mb-8 text-center">Other GTA Service Areas</h3>
            <div className="flex flex-wrap justify-center gap-3 sm:gap-4">
              {[
                { name: 'Toronto', slug: 'toronto' },
                { name: 'Mississauga', slug: 'mississauga' },
                { name: 'Brampton', slug: 'brampton' },
                { name: 'Hamilton', slug: 'hamilton' },
                { name: 'Richmond Hill', slug: 'richmond-hill' },
                { name: 'Vaughan', slug: 'vaughan' },
                { name: 'Whitby', slug: 'whitby' },
                { name: 'Ajax', slug: 'ajax' },
                { name: 'Oshawa', slug: 'oshawa' },
              ].map((city) => (
                <Link
                  key={city.slug}
                  to={`/service-areas/${city.slug}`}
                  className="bg-white px-4 sm:px-6 py-2.5 sm:py-3 rounded-full text-xs sm:text-sm font-medium text-gray-700 hover:text-[#D4A853] hover:shadow-md transition-all cursor-pointer border border-gray-200"
                >
                  {city.name}
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="bg-[#050B14] text-white py-8 sm:py-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4 sm:gap-6">
              <div className="flex items-center gap-3">
                <div className="flex flex-col leading-none">
                  <span className="text-xl sm:text-2xl font-extrabold tracking-wider text-[#D4A853]" style={{ fontFamily: "'Inter', sans-serif" }}>ALLIANCE</span>
                  <span className="text-[8px] sm:text-[10px] font-semibold tracking-[0.25em] text-[#D4A853]">SECURITY SYSTEMS</span>
                </div>
                <span className="text-white/60 text-xs sm:text-sm">Oakville Security Experts Since 1999</span>
              </div>
              <div className="flex items-center gap-4 sm:gap-6 flex-wrap justify-center">
                <Link to="/" className="text-white/60 hover:text-white text-xs sm:text-sm transition-colors cursor-pointer">Home</Link>
                <Link to="/service-areas" className="text-white/60 hover:text-white text-xs sm:text-sm transition-colors cursor-pointer">Service Areas</Link>
                <Link to="/privacy-policy" className="text-white/60 hover:text-white text-xs sm:text-sm transition-colors cursor-pointer">Privacy Policy</Link>
                <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="text-white/60 hover:text-white text-xs sm:text-sm transition-colors cursor-pointer">Powered by Readdy</a>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
}
